// src/components/Login/PasswordStrength.jsx
import React from 'react';
import { motion } from 'framer-motion';
import { Shield, ShieldAlert, ShieldCheck } from 'lucide-react';

/**
 * Componente de medidor de fuerza de contraseña profesional
 * Nivel: Google/Airbnb/Spotify
 * 
 * Características:
 * - Feedback visual en tiempo real
 * - 3 niveles de fuerza (Fraca, Média, Forte)
 * - Código de colores semáforo
 * - Animaciones suaves
 * - Iconografía intuitiva
 * - Mensajes de ayuda contextuales
 */

const PasswordStrength = ({ password = '' }) => {
  // ============================================
  // 🔒 LÓGICA DE VALIDACIÓN (PROFISSIONAL)
  // ============================================
  const calculateStrength = (pass) => {
    if (!pass) return 0;
    
    let score = 0;
    
    // ✅ CRITÉRIOS DE FORÇA (padrão indústria)
    if (pass.length >= 8) score += 1;           // Mínimo 8 caracteres
    if (pass.length >= 12) score += 1;          // Bônus por mais de 12
    if (/[A-Z]/.test(pass)) score += 1;          // Letra maiúscula
    if (/[a-z]/.test(pass)) score += 1;          // Letra minúscula
    if (/[0-9]/.test(pass)) score += 1;          // Número
    if (/[^A-Za-z0-9]/.test(pass)) score += 2;   // Caractere especial (peso 2)
    
    // Normalizar para 0-100
    const maxScore = 7; // Pontuação máxima possível
    return Math.min(100, Math.round((score / maxScore) * 100));
  };

  const strength = calculateStrength(password);
  
  // ============================================
  // 🎨 CONFIGURAÇÃO POR NÍVEL
  // ============================================
  const getStrengthConfig = () => {
    if (strength === 0) {
      return {
        level: 0,
        label: 'Digite uma senha',
        color: 'bg-gray-200',
        textColor: 'text-gray-500',
        icon: Shield,
        message: 'Mínimo 8 caracteres',
        percentage: 0
      };
    }
    
    if (strength < 40) {
      return {
        level: 1,
        label: 'Fraca',
        color: 'bg-red-500',
        textColor: 'text-red-600',
        icon: ShieldAlert,
        message: 'Adicione números e símbolos',
        percentage: 33
      };
    }
    
    if (strength < 70) {
      return {
        level: 2,
        label: 'Média',
        color: 'bg-yellow-500',
        textColor: 'text-yellow-600',
        icon: Shield,
        message: 'Boa, pode melhorar',
        percentage: 66
      };
    }
    
    return {
      level: 3,
      label: 'Forte',
      color: 'bg-emerald-500',
      textColor: 'text-emerald-600',
      icon: ShieldCheck,
      message: 'Senha excelente!',
      percentage: 100
    };
  };

  const config = getStrengthConfig();
  const Icon = config.icon;

  // ============================================
  // 🎯 VALIDAÇÕES INDIVIDUAIS (para feedback)
  // ============================================
  const validations = [
    { 
      label: 'Mínimo 8 caracteres', 
      valid: password.length >= 8,
      show: password.length > 0
    },
    { 
      label: 'Letra maiúscula', 
      valid: /[A-Z]/.test(password),
      show: password.length > 0
    },
    { 
      label: 'Letra minúscula', 
      valid: /[a-z]/.test(password),
      show: password.length > 0
    },
    { 
      label: 'Número', 
      valid: /[0-9]/.test(password),
      show: password.length > 0
    },
    { 
      label: 'Caractere especial', 
      valid: /[^A-Za-z0-9]/.test(password),
      show: password.length > 0
    }
  ];

  // Se não há senha, não mostrar nada
  if (!password) return null;

  return (
    <motion.div 
      initial={{ opacity: 0, y: -10 }}
      animate={{ opacity: 1, y: 0 }}
      className="mt-2 space-y-3"
    >
      {/* ======================================== */}
      {/* BARRA DE PROGRESSO PRINCIPAL */}
      {/* ======================================== */}
      <div className="space-y-1">
        <div className="flex items-center justify-between text-xs">
          <div className={`flex items-center gap-1.5 font-medium ${config.textColor}`}>
            <Icon className="w-3.5 h-3.5" />
            <span>{config.label}</span>
          </div>
          <span className="text-slate-500">{config.message}</span>
        </div>
        
        {/* Barra de progresso animada */}
        <div className="h-1.5 bg-slate-100 rounded-full overflow-hidden">
          <motion.div
            initial={{ width: 0 }}
            animate={{ width: `${config.percentage}%` }}
            transition={{ duration: 0.3, ease: 'easeOut' }}
            className={`h-full ${config.color} rounded-full`}
          />
        </div>
      </div>

      {/* ======================================== */}
      {/* LISTA DE VALIDAÇÕES (feedback detalhado) */}
      {/* ======================================== */}
      <motion.div 
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 0.1 }}
        className="grid grid-cols-2 gap-x-3 gap-y-1.5 text-xs"
      >
        {validations.map((item, index) => item.show && (
          <motion.div
            key={index}
            initial={{ opacity: 0, x: -5 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ delay: index * 0.05 }}
            className="flex items-center gap-1.5"
          >
            {/* Indicador visual de validação */}
            <div className={`w-1.5 h-1.5 rounded-full ${
              item.valid ? 'bg-emerald-500' : 'bg-slate-300'
            }`} />
            <span className={item.valid ? 'text-emerald-700' : 'text-slate-500'}>
              {item.label}
            </span>
          </motion.div>
        ))}
      </motion.div>

      {/* ======================================== */}
      {/* AVISO DE SEGURANÇA (apenas para senhas fracas) */}
      {/* ======================================== */}
      {config.level === 1 && (
        <motion.p
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-[11px] text-red-600/70 italic mt-1"
        >
          ⚠️ Senha fraca: fácil de adivinhar. Adicione variedade.
        </motion.p>
      )}
    </motion.div>
  );
};

// ✅ Otimização: memo para evitar re-renders desnecessários
export default React.memo(PasswordStrength);